/**
 * Pure sky-lighting math (Graphics G3) — no THREE renderer state, Node-unit-testable.
 *
 * Maps `SkyState.timeOfDay` (0..1) to a lighting band + cross-fade, and the solar
 * arc. The bands ring the day: night(0) → dawn(0.25) → day(0.5) → dusk(0.75) → night.
 */
import * as THREE from 'three/webgpu';

export type Band = 'dawn' | 'day' | 'dusk' | 'night';

/** Band order around the day (each spans a 0.25 segment of timeOfDay). */
const ORDER: Band[] = ['night', 'dawn', 'day', 'dusk'];

/** The active band, the band being crossed into, and a 0..1 cross-fade factor. */
export function bandForTimeOfDay(tod: number): { band: Band; next: Band; blend: number } {
  const t = ((tod % 1) + 1) % 1; // wrap to [0,1)
  const seg = Math.min(3, Math.floor(t / 0.25)); // 0..3 → night/dawn/day/dusk segment
  return { band: ORDER[seg], next: ORDER[(seg + 1) % 4], blend: (t - seg * 0.25) / 0.25 };
}

// ── THE SUN'S PATH: a latitude and a season (2026-09-25, realism §8 R1) ──────
// The old arc put the sun through the ZENITH every noon at every place and
// every day (`horiz = max(0.12, cos)`): no noon shadows, a flat bleached
// ground (the WebGPU "bleached noon" trim treated the symptom). The real path
// is the equatorial → horizontal transform
//   sin el = sin φ · sin δ + cos φ · cos δ · cos H
// with φ the latitude, δ the solar declination and H the hour angle (noon 0).
// The 2,048 km continent spans ~18° of latitude around LAT_CENTER_DEG (north
// = −Z, the maps' convention), so the north runs colder with a lower sun; δ
// follows a year of DAYS_PER_YEAR world days. East = +X, south = +Z: the sun
// rises in the east, culminates in the south, sets in the west.

/** Latitude at the continent's centre (absolute Z = 0), degrees north. */
export const LAT_CENTER_DEG = 42;
/** Metres per degree of latitude. */
export const METRES_PER_DEG_LAT = 111_320;
/** The seasonal year, in world days (one world day = one sky cycle). */
export const DAYS_PER_YEAR = 365;
/** Day of year of the March equinox (δ = 0, rising). */
export const EQUINOX_DOY = 80;
/** Earth's obliquity — the declination amplitude. */
export const OBLIQUITY_DEG = 23.44;

/** Latitude (degrees north) at an ABSOLUTE world Z — north is −Z. */
export function latitudeForZ(absZ: number): number {
  const lat = LAT_CENTER_DEG - (Number.isFinite(absZ) ? absZ : 0) / METRES_PER_DEG_LAT;
  return Math.max(-89, Math.min(89, lat));
}

/** Solar declination (degrees) on a day of the year. */
export function solarDeclinationDeg(dayOfYear: number): number {
  return OBLIQUITY_DEG * Math.sin((2 * Math.PI * (dayOfYear - EQUINOX_DOY)) / DAYS_PER_YEAR);
}

/** The world's day of year from the shared wall clock and the sky's cycle length (every client agrees). */
export function worldDayOfYear(nowSec: number, cycleSeconds = 7200): number {
  const days = nowSec / Math.max(60, cycleSeconds);
  return ((days % DAYS_PER_YEAR) + DAYS_PER_YEAR) % DAYS_PER_YEAR;
}

/** Noon solar elevation (degrees) at a latitude and declination. */
export function noonElevationDeg(latDeg: number, declDeg: number): number {
  return 90 - Math.abs(latDeg - declDeg);
}

/**
 * Unit direction TO the sun at time-of-day `tod` (0 midnight, 0.5 noon) for an
 * observer at `latDeg` on a day of declination `declDeg`. World axes: +X east,
 * +Y up, +Z south (north = −Z).
 */
export function sunDirectionAt(tod: number, latDeg: number, declDeg: number): { x: number; y: number; z: number } {
  const H = (tod - 0.5) * Math.PI * 2;
  const phi = latDeg * Math.PI / 180, dec = declDeg * Math.PI / 180;
  const sd = Math.sin(dec), cd = Math.cos(dec), sp = Math.sin(phi), cp = Math.cos(phi), cH = Math.cos(H);
  const east = -cd * Math.sin(H);
  const north = sd * cp - cd * cH * sp;
  const up = sd * sp + cd * cH * cp;
  return { x: east, y: up, z: -north };
}

/** The observer every `sunDirection(tod)` consumer shares (LightingModule drives it from the player's Z + the world day). */
const observer = { latDeg: LAT_CENTER_DEG, dayOfYear: EQUINOX_DOY, declDeg: 0, sp: 0, cp: 1, sd: 0, cd: 1, pinnedDoy: null as number | null, pinnedLat: null as number | null };
function refreshObserver(): void {
  const lat = observer.pinnedLat ?? observer.latDeg;
  const doy = observer.pinnedDoy ?? observer.dayOfYear;
  observer.declDeg = solarDeclinationDeg(doy);
  const phi = lat * Math.PI / 180, dec = observer.declDeg * Math.PI / 180;
  observer.sp = Math.sin(phi); observer.cp = Math.cos(phi); observer.sd = Math.sin(dec); observer.cd = Math.cos(dec);
}
refreshObserver();

/** Move the shared observer (latitude from the player's absolute Z, the world's day of year). Cheap; call per frame. */
export function setSunObserver(latDeg: number, dayOfYear: number): void {
  if (Math.abs(latDeg - observer.latDeg) < 1e-4 && Math.abs(dayOfYear - observer.dayOfYear) < 1e-4) return;
  observer.latDeg = latDeg; observer.dayOfYear = dayOfYear;
  refreshObserver();
}
/** Probe pin: hold the season (and optionally the latitude) — null releases. */
export function pinSunSeason(dayOfYear: number | null, latDeg: number | null = null): void {
  observer.pinnedDoy = dayOfYear; observer.pinnedLat = latDeg;
  refreshObserver();
}
/** What the shared observer is (the lighting lever reports it). */
export function sunObserver(): { latDeg: number; dayOfYear: number; declDeg: number; pinned: boolean } {
  return { latDeg: observer.pinnedLat ?? observer.latDeg, dayOfYear: observer.pinnedDoy ?? observer.dayOfYear, declDeg: observer.declDeg, pinned: observer.pinnedDoy !== null || observer.pinnedLat !== null };
}

/** Solar elevation sin in [-1, 1] (negative = the sun is below the horizon = night). */
export function sunElevation(tod: number): number {
  const cH = Math.cos((tod - 0.5) * Math.PI * 2);
  return observer.sd * observer.sp + observer.cd * cH * observer.cp;
}

/** World position for the sun DirectionalLight along its day path (light points at the origin).
 *  Pass `out` from per-frame callers to skip the Vector3 allocation. Below the horizon y < 0. */
export function sunPosition(tod: number, dist = 120, out?: THREE.Vector3): THREE.Vector3 {
  const H = (tod - 0.5) * Math.PI * 2;
  const cH = Math.cos(H);
  const east = -observer.cd * Math.sin(H);
  const north = observer.sd * observer.cp - observer.cd * cH * observer.sp;
  const up = observer.sd * observer.sp + observer.cd * cH * observer.cp;
  return (out ?? new THREE.Vector3()).set(east * dist, up * dist, -north * dist);
}

/** Linear interpolate two sRGB-hex colors → a THREE.Color (linear). */
export function lerpColorHex(aHex: string, bHex: string, t: number): THREE.Color {
  return lerpColorHexInto(aHex, bHex, t, new THREE.Color());
}

/** Allocation-free lerpColorHex — per-frame callers pass their own scratch. */
export function lerpColorHexInto(aHex: string, bHex: string, t: number, out: THREE.Color): THREE.Color {
  out.set(aHex);
  _lerpScratch.set(bHex);
  return out.lerp(_lerpScratch, THREE.MathUtils.clamp(t, 0, 1));
}
const _lerpScratch = new THREE.Color();

// ── Day/night driver (Phase B keystone) ─────────────────────────────────────
// Pure functions consumed by Tasks 3-6.  sunY = sin(elevation) = sunDirection.y.
// Research twilight anchors: civil dusk ≈ +0.04, nautical dusk ≈ -0.07,
// astronomical dusk ≈ -0.16; milky way visible only in astronomical dark ≈ -0.28.

/** Smoothstep (Hermite) clamped to [0, 1]. */
function smooth(t: number): number { const x = Math.max(0, Math.min(1, t)); return x * x * (3 - 2 * x); }

/** Normalized unit direction FROM origin TO the sun, as a plain {x,y,z} object.
 *  The returned literal stays fresh (callers may retain it); the two INNER
 *  Vector3s this used to allocate per call now reuse module scratch. */
const _sunDirScratch = new THREE.Vector3();
export function sunDirection(tod: number): { x: number; y: number; z: number } {
  const v = sunPosition(tod, 120, _sunDirScratch).normalize();
  return { x: v.x, y: v.y, z: v.z };
}

/**
 * 0 when the sun is clearly up, 1 when clearly below the horizon.
 * Ramps via smoothstep from civil twilight (+0.04) down through nautical (-0.16).
 */
export function nightFactor(sunY: number): number { return smooth((0.04 - sunY) / (0.04 - -0.16)); }

/**
 * The FLAT sky's fallback colour (minimal tier, `SkyModule.initFlat`) when no
 * atmosphere fog colour is available: the day-band haze at noon, the night
 * band at midnight, by the night factor. Pure — the harness pins the ramp.
 */
export const FLAT_SKY_DAY_HEX = '#accfe6';
export const FLAT_SKY_NIGHT_HEX = '#161e36';
export function flatSkyFallbackColor(nightFactor01: number, out: THREE.Color): THREE.Color {
  return lerpColorHexInto(FLAT_SKY_DAY_HEX, FLAT_SKY_NIGHT_HEX, nightFactor01, out);
}

/**
 * Lags nightFactor — milky way / nebula visible only in astronomical darkness.
 * Ramps from nautical twilight (-0.10) down through astronomical (-0.28).
 */
export function deepNight(sunY: number): number { return smooth((-0.10 - sunY) / (-0.10 - -0.28)); }

/**
 * Step a time-of-day value (0..1) by `delta`, wrapping into [0,1). Used by the
 * day/night preview keys + the debug menu to nudge the Local time override
 * (0.0 = midnight, 0.5 = noon). The double-mod handles negative deltas.
 */
export function stepTod(t: number, delta: number): number {
  return ((t + delta) % 1 + 1) % 1;
}
